import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import { Route as rootRoute } from './routes/__root';
import { Route as indexRoute } from './routes/index';
import { Route as cartRoute } from './routes/cart';
import { Route as checkoutRoute } from './routes/checkout';
import { Route as mockCheckoutRoute } from './routes/mock-checkout';
import { Route as adminRoute } from './routes/admin/index';
import { Route as loginRoute } from './routes/login';
import { Route as profileRoute } from './routes/profile';
import { Route as aboutRoute } from './routes/about';
import { Route as termsRoute } from './routes/terms';
import { Route as privacyRoute } from './routes/privacy';
import { Route as sellRoute } from './routes/sell';
import { Route as vendorRoute } from './routes/vendor/index';
import { Route as vendorProfileRoute } from './routes/shop/$vendorId';
import { Route as productRoute } from './routes/product/$productId';
import { initAppCheck, auth } from './firebase';
import { useAuthStore } from './store';
import { onAuthStateChanged } from 'firebase/auth';

const routeTree = rootRoute.addChildren([
  indexRoute,
  cartRoute,
  checkoutRoute,
  mockCheckoutRoute,
  adminRoute,
  loginRoute,
  profileRoute,
  aboutRoute,
  termsRoute,
  privacyRoute,
  sellRoute,
  vendorRoute,
  vendorProfileRoute,
  productRoute
]);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function App() {
  const { setUser, setAuthReady, fetchProfile } = useAuthStore();

  useEffect(() => {
    // Initialize App Check early
    initAppCheck();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('onAuthStateChanged user:', user);
      setUser(user);
      if (user) {
        console.log('Fetching profile for:', user.uid);
        await fetchProfile(user.uid);
      } else {
        console.log('No user logged in');
      }
      setAuthReady(true);
    });

    return () => unsubscribe();
  }, [setUser, setAuthReady, fetchProfile]);

  return (
    <HelmetProvider>
      <RouterProvider router={router} />
    </HelmetProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
